import React from "react";
import "./WorkoutForm.css";

const CreateExerciseModal = ({
  cmodal,
  setCmodal,
  newExercise,
  handleNewExerciseChange,
  generalFields,
  selectedFields,
  handleFieldSelection,
  createNewExercise,
  // Optional props to support video upload similar to WorkoutForm
  selectedVideoUrl,
  handleDivClick,
  fileInputRef,
  handleSelectVideo,
  isUpLoading,
  progress,
}) => {
  // Move hooks to top-level to comply with Rules of Hooks
  const [localVideoUrl, setLocalVideoUrl] = React.useState(null);
  const internalFileRef = React.useRef(null);

  // Update local preview when user selects a file via the parent input
  React.useEffect(() => {
    const input = fileInputRef?.current;
    if (!input) return;

    const onExternalChange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        if (localVideoUrl) {
          try { URL.revokeObjectURL(localVideoUrl); } catch (e) {}
        }
        const url = URL.createObjectURL(file);
        setLocalVideoUrl(url);
      }
    };

    input.addEventListener('change', onExternalChange);
    return () => {
      input.removeEventListener('change', onExternalChange);
    };
  }, [fileInputRef, localVideoUrl]);

  // Clear local preview and input when modal closes
  React.useEffect(() => {
    if (!cmodal) {
      if (localVideoUrl) {
        try { URL.revokeObjectURL(localVideoUrl); } catch (e) {}
      }
      setLocalVideoUrl(null);
      if (internalFileRef.current) internalFileRef.current.value = "";
      if (fileInputRef?.current) fileInputRef.current.value = "";
    }
  }, [cmodal]);

  if (!cmodal) return null;

  // Local-only preview to avoid stale parent video showing
  const effectiveSelectedVideoUrl = localVideoUrl;

  const onDivClick = () => {
    if (handleDivClick) return handleDivClick();
    internalFileRef.current && internalFileRef.current.click();
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (localVideoUrl) {
        try { URL.revokeObjectURL(localVideoUrl); } catch (e) {}
      }
      const url = URL.createObjectURL(file);
      setLocalVideoUrl(url);
    }
    if (handleSelectVideo) return handleSelectVideo(e);
    // No-op fallback if parent handler not provided
  };

  const effectiveFileRef = fileInputRef || internalFileRef;
  const uploading = Boolean(isUpLoading);
  const uploadProgress = Math.round(progress || 0);

  return (
    <div className="excercise-modal">
      <div className="child-modal">
        <div className="add-excersie">
          <span></span>
          <i className="fa fa-times" onClick={() => setCmodal(false)}></i>
        </div>
        <div className="exercise-form">
          <div className="form-group mb-3">
            <input
              type="text"
              name="name"
              className="form-control input"
              value={newExercise.name}
              onChange={handleNewExerciseChange}
              placeholder="Exercise Name"
            />
          </div>
          <div className="form-group mb-3">
            <input
              type="text"
              className="form-control input"
              name="video"
              value={
                newExercise.video && newExercise.video.includes("firebasestorage.googleapis.com")
                  ? ""
                  : newExercise.video
              }
              onChange={handleNewExerciseChange}
              placeholder="Video Link"
            />
            <small className="pw_upload-help">If you already have a video link, you can add it here</small>
          </div>

          {/* Video Upload (styled like WorkoutForm) */}
          <div className={`pw_upload-container ${effectiveSelectedVideoUrl || newExercise.video ? "pw_has-media" : ""}`}>
            <label className="pw_upload-label">Exercise Video</label>
            <span className="pw_upload-help">Upload a video or use the link field above</span>

            {(effectiveSelectedVideoUrl || newExercise.video) && (
              <button
                type="button"
                className="pw_change-btn"
                onClick={onDivClick}
                title="Change video"
              >
                <i className="fa fa-pencil"></i>
              </button>
            )}

            <div onClick={onDivClick} style={{ cursor: "pointer" }}>
              <input
                ref={effectiveFileRef}
                type="file"
                accept="video/*"
                style={{ display: "none" }}
                onChange={onFileChange}
              />

              {!effectiveSelectedVideoUrl && !newExercise.video ? (
                <>
                  <div className="pw_upload-icon">
                    <i className="fa fa-video-camera"></i>
                  </div>
                  <div className="pw_upload-text">Upload Video</div>
                  <div className="pw_upload-subtext">
                    Click to select an exercise demonstration video
                  </div>
                </>
              ) : effectiveSelectedVideoUrl ? (
                <>
                  <video className="pw_video-preview" controls src={effectiveSelectedVideoUrl} />
                  <div className="pw_upload-subtext">Click to change video</div>
                </>
              ) : (
                <>
                  {newExercise.video && !(
                    newExercise.video.includes("youtube.com") ||
                    newExercise.video.includes("youtu.be")
                  ) && (
                    <video className="pw_video-preview" controls src={newExercise.video} />
                  )}
                  <div className="pw_upload-subtext">Click to change video</div>
                </>
              )}

              {uploading && (
                <div className="pw_upload-progress">
                  <div
                    className="pw_progress-bar"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-3" style={{ marginTop: "1rem" }}>
            <label>Select Fields (up to 2)</label>
            <ul>
              {generalFields?.map((field) => (
                <li key={field.measurement_id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedFields?.some(
                        (selectedField) =>
                          selectedField.measurement_id === field.measurement_id
                      )}
                      onChange={() => handleFieldSelection(field)}
                      disabled={
                        selectedFields.length >= 2 &&
                        !selectedFields.some(
                          (selectedField) =>
                            selectedField.measurement_id === field.measurement_id
                        )
                      }
                    />
                    {field.field}
                  </label>
                </li>
              ))}
            </ul>
          </div>
          <button className="button" onClick={() => createNewExercise()}>
            Create Exercise
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateExerciseModal;
